import axios from "axios";

const API_URL = import.meta.env.VITE_APP_API_URL;

const handleResponse = (response: any) => {
	const data = response.data?.data;
	if (!data) return [];
	return Array.isArray(data) ? data : data?.items || [];
};

export const fetchGenders = async () => {
	const response = await axios.get(`${API_URL}/admin/master/genders/list`);
	return handleResponse(response);
};

export const fetchRoles = async () => {
	const response = await axios.get(`${API_URL}/admin/master/roles/list`);
	return handleResponse(response);
};

export const fetchLevels = async () => {
	const response = await axios.get(`${API_URL}/admin/master/levels/list`);
	return handleResponse(response);
};

export const fetchUrbanBodyTypes = async () => {
	const response = await axios.get(
		`${API_URL}/admin/master/urban-body-types/list`,
	);
	return handleResponse(response);
};

// export const fetchStates = async () => {
//     const response = await axios.get(`${API_URL}/admin/master/states/list`);
//     return handleResponse(response);
// };

export const fetchDistricts = async () => {
	const response = await axios.get(`${API_URL}/admin/master/districts`);
	return handleResponse(response);
};

export const fetchBlocks = async (districtId?: string | number) => {
	const url = districtId
		? `${API_URL}/admin/master/blocks/${districtId}`
		: `${API_URL}/admin/master/blocks`;
	const response = await axios.get(url);
	return handleResponse(response);
};

export const fetchBlocksList = async () => {
	const response = await axios.get(`${API_URL}/admin/master/blocks`);
	return handleResponse(response);
};

export const fetchBloodGroups = async () => {
	const response = await axios.get(`${API_URL}/admin/master/blood-groups/list`);
	return handleResponse(response);
};

export const fetchCastes = async () => {
	const response = await axios.get(`${API_URL}/admin/master/castes/list`);
	return handleResponse(response);
};

export const fetchRecruitmentTypes = async () => {
	const response = await axios.get(
		`${API_URL}/admin/master/recruitment-types/list`,
	);
	return handleResponse(response);
};

export const fetchReligions = async () => {
	const response = await axios.get(`${API_URL}/admin/master/religions/list`);
	return handleResponse(response);
};

export const fetchQualifications = async () => {
	const response = await axios.get(`${API_URL}/admin/master/qualifications/list`);
	return handleResponse(response);
};

export const fetchSchemes = async () => {
	const response = await axios.get(`${API_URL}/admin/master/schemes/list`);
	return handleResponse(response);
};

export const fetchVillages = async (blockId: string | number) => {
	const response = await axios.get(
		`${API_URL}/admin/master/villages/${blockId}`,
	);
	return handleResponse(response);
};

/* --------- EMPLOYEE REGISTRATION MULTI-STEP API --------- */

export const savePersonalDetails = async (
	payload: any,
	id?: string | number,
) => {
	// Step 1: POST or PUT based on presence of id
	if (id) {
		return axios.put(`${API_URL}/admin/employee-detail/edit/${id}`, payload);
	}
	return axios.post(
		`${API_URL}/admin/employee-detail/personal-details`,
		payload,
	);
};

export const saveFamilyDetails = async (id: string | number, payload: any) => {
	// Step 2: PUT
	return axios.put(
		`${API_URL}/admin/employee-detail/family-details/${id}`,
		payload,
	);
};

export const saveEducationExperience = async (
	id: string | number,
	payload: any,
) => {
	// Step 3: PUT
	return axios.put(
		`${API_URL}/admin/employee-detail/education-and-experience/${id}`,
		payload,
	);
};

export const saveWorkingDetails = async (id: string | number, payload: any) => {
	// Step 4: PUT
	return axios.put(
		`${API_URL}/admin/employee-detail/working-details/${id}`,
		payload,
	);
};

export const saveTrainingAuditDetails = async (
	id: string | number,
	payload: any,
) => {
	// Step 5: PUT
	return axios.put(
		`${API_URL}/admin/employee-detail/training-audit-details/${id}`,
		payload,
	);
};

/* --------- GENERAL EMPLOYEE API --------- */

export const fetchEmployeeList = async (
	params: {
		page?: number;
		limit?: number;
		search?: string;
		type_id?: string | number;
		district_id?: string | number;
		status?: string;
	} = {},
) => {
	const { page = 1, limit = 10, search = "", type_id, district_id, status } = params;
	const isStateOfficerList = type_id === 2 || type_id === "2";
	const endpoint = isStateOfficerList ? "state-officer-list" : "list";
	let url = `${API_URL}/admin/employee-detail/${endpoint}?page=${page}&limit=${limit}&search=${search}`;
	if (type_id && !isStateOfficerList) {
		url += `&type_id=${type_id}`;
	}
	if (district_id) {
		url += `&district_id=${district_id}`;
	}
	if (status) {
		url += `&status=${status}`;
	}
	const response = await axios.get(url);
	return response.data?.data;
};

export const fetchEmployeeById = async (id: string | number) => {
	return axios.get(`${API_URL}/admin/employee-detail/view/${id}`);
};

export const deleteEmployee = async (id: string | number) => {
	return axios.delete(`${API_URL}/admin/employee-detail/delete/${id}`);
};

export const uploadProfilePhoto = async (file: File) => {
	const formData = new FormData();
	formData.append("filename", file);
	return axios.post(`${API_URL}/upload/upload-single/profile-photo`, formData, {
		headers: { "Content-Type": "multipart/form-data" },
		withCredentials: true,
	});
};

export const uploadAadhaarCard = async (file: File) => {
	const formData = new FormData();
	formData.append("filename", file);
	return axios.post(`${API_URL}/upload/upload-single/aadhar-card`, formData, {
		headers: { "Content-Type": "multipart/form-data" },
		withCredentials: true,
	});
};

export const uploadPanCard = async (file: File) => {
	const formData = new FormData();
	formData.append("filename", file);
	return axios.post(`${API_URL}/upload/upload-single/pan-card`, formData, {
		headers: { "Content-Type": "multipart/form-data" },
		withCredentials: true,
	});
};

export const uploadDisabilityCertificate = async (file: File) => {
	const formData = new FormData();
	formData.append("filename", file);
	return axios.post(
		`${API_URL}/upload/upload-single/differently-abled`,
		formData,
		{
			headers: { "Content-Type": "multipart/form-data" },
			withCredentials: true,
		},
	);
};

export const uploadHealthCertificate = async (file: File) => {
	const formData = new FormData();
	formData.append("filename", file);
	return axios.post(`${API_URL}/upload/upload-single/health-status`, formData, {
		headers: { "Content-Type": "multipart/form-data" },
		withCredentials: true,
	});
};
export const uploadMedicalCertificate = async (file: File) => {
	const formData = new FormData();
	formData.append("filename", file);
	return axios.post(
		`${API_URL}/upload/upload-single/medical-certificate`,
		formData,
		{
			headers: { "Content-Type": "multipart/form-data" },
			withCredentials: true,
		},
	);
};
export const uploadBankPassbook = async (file: File) => {
	const formData = new FormData();
	formData.append("filename", file);
	return axios.post(`${API_URL}/upload/upload-single/bank-passbook`, formData, {
		headers: { "Content-Type": "multipart/form-data" },
		withCredentials: true,
	});
};

export const uploadAcademicCertificate = async (file: File) => {
	const formData = new FormData();
	formData.append("filename", file);
	return axios.post(
		`${API_URL}/upload/upload-single/academic-certificate`,
		formData,
		{
			headers: { "Content-Type": "multipart/form-data" },
			withCredentials: true,
		},
	);
};

export const uploadExperienceCertificate = async (file: File) => {
	const formData = new FormData();
	formData.append("filename", file);
	return axios.post(
		`${API_URL}/upload/upload-single/experience-certificate`,
		formData,
		{
			headers: { "Content-Type": "multipart/form-data" },
			withCredentials: true,
		},
	);
};

/* --------- COMPLAINT API --------- */

export const saveComplaint = async (id: string | number, payload: any) => {
	return axios.put(
		`${API_URL}/admin/employee-detail/complaints/${id}`,
		payload,
	);
};

export const fetchComplaints = async (id: string | number) => {
	const response = await axios.get(
		`${API_URL}/admin/employee-detail/complaints-list/${id}`,
	);
	return handleResponse(response);
};

export const uploadComplaintFile = async (file: File) => {
	const formData = new FormData();
	formData.append("filename", file);
	return axios.post(`${API_URL}/upload/upload-single/complaint`, formData, {
		headers: { "Content-Type": "multipart/form-data" },
		withCredentials: true,
	});
};
